// @eidollon/contracts/gdelt-codes
//
// GDELT event decoding — turns a stored CAMEO code + actors + location into a
// human sentence ("Use conventional military force — RUSSIA → UKRAINE ·
// Ukraine") at DISPLAY time, from the signal's payload.
//
// Why decode at render, not at ingest: the description is derived from the
// canonical map below, NOT baked into the row. Improve the map and every
// event — past and future — updates on next render, with zero backfill and
// zero cache flush. The single source of truth is cameo-codes.json, loaded
// here for TS (API + browser, bundled) and by the Python connector
// (eidollon_etl.sources._gdelt_codes) — same file, no drift.
//
// Iron Rule #8: no plugin registry, no per-call lookup table rebuild — the
// JSON is imported once and the maps are module constants.

import cameo from './cameo-codes.json';

const ROOTS = cameo.roots as Record<string, string>;
const EVENTS = cameo.events as Record<string, string>;
const COUNTRIES = cameo.countries as Record<string, string>;

/** Most-specific CAMEO label for a code: 4-digit event → 3-digit base →
 *  2-digit root. Returns null if nothing matches (caller decides fallback). */
export function cameoLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  const c = String(code).trim();
  if (!c) return null;
  return EVENTS[c] ?? EVENTS[c.slice(0, 3)] ?? ROOTS[c.slice(0, 2)] ?? null;
}

/** FIPS 10-4 country code → name; unmapped codes return the raw code. */
export function countryName(code: string | null | undefined): string | null {
  if (!code) return null;
  const c = String(code).trim().toUpperCase();
  return c ? (COUNTRIES[c] ?? c) : null;
}

function str(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s || null;
}

/** Compose a GDELT event title from its payload. Returns null if the payload
 *  carries no usable code (caller falls back to the stored summary). */
export function gdeltTitle(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload) return null;
  const code = str(payload['event_code']) ?? str(payload['event_root_code']);
  const label = cameoLabel(code);
  if (!label && !code) return null;

  const a1 = str(payload['actor1']);
  const a2 = str(payload['actor2']);
  const actors = a1 && a2 ? `${a1} → ${a2}` : (a1 ?? a2);
  const place = countryName(str(payload['action_country']));

  const head = label ?? `Event ${code}`;
  const main = actors ? `${head} — ${actors}` : head;
  return place ? `${main} · ${place}` : main;
}

/** The operator-facing title for any signal. GDELT events are decoded from
 *  payload at render time (always current); every other source uses its
 *  stored summary. Always returns a non-empty string. */
export function signalTitle(signal: {
  source: string;
  summary: string;
  payload?: Record<string, unknown> | null;
}): string {
  if (signal.source === 'GDELT') {
    const derived = gdeltTitle(signal.payload);
    if (derived) return derived;
  }
  return signal.summary;
}
